import {useEffect, useRef} from 'react'
import {useQueryClient} from '@tanstack/react-query'

import {RQKEY, RQKEY_LINKED_ACCOUNTS} from '#/state/queries/profile'
import {useVerificationStatusQuery} from '#/state/queries/verification-status'
import {useSession} from '#/state/session'

/**
 * Polls `getVerificationStatus` on `installWid`, `widSetup`, and `verifying`
 * steps and auto-advances via caller-provided callbacks:
 * - `onScanned` fires once when `inviteScanned` first becomes true while on
 *   `installWid` or `widSetup`.
 * - `onVerified` fires once when `verified` first becomes true while on
 *   `verifying`, after invalidating session / profile / linked-accounts
 *   queries so downstream reads reflect the upgraded account.
 */
export function useWidVerificationAutoAdvance({
  step,
  onScanned,
  onVerified,
}: {
  step: string
  onScanned: () => void
  onVerified: () => void
}) {
  const {currentAccount} = useSession()
  const queryClient = useQueryClient()

  const {data} = useVerificationStatusQuery({
    enabled:
      step === 'installWid' || step === 'widSetup' || step === 'verifying',
  })

  const scannedFiredRef = useRef(false)
  const verifiedFiredRef = useRef(false)

  useEffect(() => {
    if (
      (step === 'installWid' || step === 'widSetup') &&
      data?.inviteScanned === true &&
      !scannedFiredRef.current
    ) {
      scannedFiredRef.current = true
      onScanned()
    }
  }, [step, data?.inviteScanned, onScanned])

  useEffect(() => {
    if (
      step === 'verifying' &&
      data?.verified === true &&
      !verifiedFiredRef.current
    ) {
      verifiedFiredRef.current = true
      const did = currentAccount?.did
      if (did) {
        void queryClient.invalidateQueries({
          queryKey: ['curr-user-session', did],
        })
        void queryClient.invalidateQueries({queryKey: RQKEY(did)})
        void queryClient.invalidateQueries({
          queryKey: RQKEY_LINKED_ACCOUNTS(did),
        })
      }
      onVerified()
    }
  }, [step, data?.verified, onVerified, currentAccount?.did, queryClient])
}
